<template>
	<div class="eia-map-wrapper">
		<div class="eia-map">
			<div
				class="eia-map__map-container"
				ref="map">
			</div>
		</div>
		<div class="eia-map-wrapper__search">
			<eia-validated-input
				id="map-postcode"
				ref="postcodeInput"
				type="text"
				:validators="validators"
				:show-error-indentation="true"
				placeholder="Enter postcode eg. AB12 3BC"
				:value="postcode"
				@enter="findPostcode"
				@input="onPostcodeInput">
			</eia-validated-input>
			<eia-button
				class="button-back"
				variation="primary"
				size="large"
				:disabled="postcodeLoading"
				@click="findPostcode">
				<slot name="button-text">Search</slot>
			</eia-button>
		</div>
	</div>
</template>

<script>
import config from '@/config';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import mapboxDrawStyle from '@/config/mapboxDrawStyle';
import { mapGetters, mapMutations } from 'vuex';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { onRequestComplete } from '@/utils/api-utils';

export default {
	name: 'eia-map',

	data() {
		return {
			center: [ -1, 51 ],
			zoom: 8,
			map: null,
			draw: null,
			postcode: '',
			postcodeLoading: false,
			postcodeError: null,
		};
	},

	computed: {
		...mapGetters( 'stages', [ 'geoJSONBoundary' ] ),

		validators() {
			return [
				() => {
					return this.postcodeError;
				},
			];
		},

		mapStyleUrl() {
			return `https://api.maptiler.com/maps/${config.maptilerStyleId}/style.json?key=${config.maptilerAPIKey}`;
		},

		mapConfig() {
			const mapConfig = {
				container: this.$refs.map,
				style: this.mapStyleUrl,
				fadeDuration: 50,
			};

			if ( Array.isArray( this.center ) && this.center.length === 2 ) {
				mapConfig.center = this.center;
			}

			if ( typeof this.zoom === "number" && this.zoom !== -1 ) {
				mapConfig.zoom = this.zoom;
			}

			return mapConfig;
		},
	},

	async mounted() {
		this.onDrawChange = this.onDrawChange.bind( this );

		// Now that we know if we have geolocation or not,
		// we can continue on our map initialising adventures...
		await this.initialise();
		this.onMapLoaded();
	},

	methods: {

		...mapMutations( 'stages', [ 'storeGeoJSONBoundary' ] ),

		onPostcodeInput( value ) {
			this.postcodeError = false;
			this.postcode = value;
		},

		async findPostcode(){

			this.postcodeLoading = true;

			try{
				const res = await fetch( 'https://api.getthedata.com/postcode/'+encodeURIComponent( this.postcode ) );

				const data = await onRequestComplete( res );

				if( data.status === 'match' ){

					this.map.panTo( [ data.data.longitude, data.data.latitude  ], {
						zoom: 15,
					} );

				}
				else if( data.error ){
					this.postcodeError = data.error;
					this.$refs.postcodeInput.onBlur( true );
				}

			}
			catch( e ){
				console.log( e );
			}

			this.postcodeLoading = false;

		},

		async initialise() {
			return new Promise( resolve => {
				if( !this.map ) {
					this.map = this.createMapboxInstance();
				}

				if( !this.draw ) {
					this.draw = this.createDrawInstance();
					this.map.addControl( this.draw );
				}

				this.mapGeolocationControl = new mapboxgl.GeolocateControl( {
					positionOptions: { enableHighAccuracy: true },
					fitBoundsOptions: { maxZoom: 14 },
					trackUserLocation: true,
				} );

				this.map.addControl( this.mapGeolocationControl );

				this.map.on( 'draw.create', this.onDrawChange );
				this.map.on( 'draw.delete', this.onDrawChange );
				this.map.on( 'draw.update', this.onDrawChange );

				this.map.on( 'load', resolve );
			} );
		},

		createMapboxInstance() {
			const mapConfig = {
				container: this.$refs.map,
				style: this.mapStyleUrl,
				fadeDuration: 50,
			};

			if ( Array.isArray( this.center ) && this.center.length === 2 ) {
				mapConfig.center = this.center;
			}

			if ( typeof this.zoom === "number" && this.zoom !== -1 ) {
				mapConfig.zoom = this.zoom;
			}

			return new mapboxgl.Map( mapConfig );
		},

		createDrawInstance() {
			const drawInstance = new MapboxDraw( {
				styles: mapboxDrawStyle,
				clickBuffer: 5,
				touchBuffer: 25,
				displayControlsDefault: false,
				defaultMode: 'draw_polygon',
				controls: {
					polygon: true,
					trash: true,
					point: false,
					line_string: false, // eslint-disable-line
					combine_features: false, // eslint-disable-line
					uncombine_features: false, // eslint-disable-line
				},
			} );

			return drawInstance;
		},

		onMapLoaded() {
			// Reinstantiate any saved geojson polygons here via
			// the mapped `geoJSONBoundary` getter from VueX.
			if( this.geoJSONBoundary ) {
				this.draw.add( this.geoJSONBoundary );
			}

			// Request geolocation
			this.mapGeolocationControl.trigger();
		},

		onDrawChange() {
			const data = this.draw.getAll();

			if ( data.features.length > 0 ) {
				this.storeGeoJSONBoundary( data );
			}
			else {
				this.storeGeoJSONBoundary( null );
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.eia-map-wrapper{

	.eia-map-wrapper__search {
		display: flex;
		flex-direction: row;

		.eia-validated-input{
			flex: 1;
		}
		.eia-button{
			height: 2.9rem;
			margin-top: 0.85rem;
			margin-left: 1rem;
			min-width: 150px;
		}
	}

	.eia-map {
		position: relative;
		min-height: 62.5vh;
		min-width: 100%;

		@media screen and ( max-width: $screen-xs ) {
			min-height: 50vh;
		}

		&__map-container {
			position: absolute;
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
			height: 100%;
			background: $color-grey-light;
		}
	}
}
</style>
